import { useEffect, useState } from "react";

/**
 * netStatus — Jun 2026 · Offline Grace.
 *
 * One source of truth for "are we reachable". Two signals feed it:
 *   1. the browser's own online/offline events, and
 *   2. axios responses — a request that comes back with NO response is a
 *      network failure (the browser can be "online" and still unreachable),
 *      any successful response clears the flag.
 *
 * Screens keep rendering their last-known data while this is true; the slim
 * banner (see components/OfflineBanner.jsx) is the only chrome that changes.
 */
let offline = false;
const listeners = new Set();
const wired = new WeakSet();

export function isOffline() {
  return offline;
}

export function setOffline(next) {
  if (offline === next) return;
  offline = next;
  listeners.forEach((fn) => fn(next));
}

export function subscribeNet(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function wireNetStatus(api) {
  if (!api?.interceptors?.response || wired.has(api)) return;
  wired.add(api);
  api.interceptors.response.use(
    (res) => {
      setOffline(false);
      return res;
    },
    (err) => {
      // No `response` = network error / DNS / CORS blip / aborted.
      if (!err?.response) setOffline(true);
      else setOffline(false);
      return Promise.reject(err);
    },
  );
}

export function useNetStatus() {
  const [off, setOff] = useState(offline);

  useEffect(() => {
    const unsub = subscribeNet(setOff);
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    if (typeof navigator !== "undefined" && navigator.onLine === false) setOffline(true);
    return () => {
      unsub();
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  return off;
}
